import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import CyberBackground from "../components/CyberBackground";
import ConsultForm from "../components/ConsultForm";
import CTABand from "../components/resource/CTABand";
import { PLATFORM_DETAILS, PLATFORM_ORDER } from "../i18n/platformDetails";
import { container, item, inView } from "../components/service/presets";
import { ArrowLeft, Check, ArrowRight } from "lucide-react";

const HEADINGS = {
  en: ["Key Features", "Core Components", "Key Benefits", "How RoboQuess Helps"],
  ar: ["الميزات الرئيسية", "المكوّنات الأساسية", "أبرز الفوائد", "كيف تساعدك روبوكويس"],
};

export default function PlatformDetail() {
  const { slug } = useParams();
  const { dir, lang } = useLang();
  const navigate = useNavigate();
  const flip = dir === "rtl" ? "scaleX(-1)" : "none";
  const entry = PLATFORM_DETAILS[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!entry) navigate("/platforms");
  }, [entry, navigate, slug]);

  if (!entry) return null;

  const c = entry[lang] || entry.en;
  const labels = HEADINGS[lang] || HEADINGS.en;
  const sections = c.sections
    .map((s, i) => ({ ...s, heading: labels[i] || labels[labels.length - 1] }))
    .filter((s) => (s.paras && s.paras.length) || (s.list && s.list.length));
  const others = PLATFORM_ORDER.filter((s) => s !== slug).slice(0, 8);

  return (
    <div>
      {/* HERO */}
      <section className="relative pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0 rq-grid-bg opacity-40" />
        <div className="absolute inset-0 opacity-50"><CyberBackground /></div>
        <div className="rq-container relative z-10 max-w-3xl">
          <Link to="/platforms" className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--rq-green)] mb-6" data-testid="platform-back-link">
            <ArrowLeft size={16} style={{ transform: flip }} />
            {lang === "ar" ? "العودة إلى المنصّات" : "Back to Platforms"}
          </Link>
          <div className="flex items-center gap-4 mb-5">
            <div className="h-16 w-16 rounded-2xl overflow-hidden ring-1 ring-black/10 shadow-lg shrink-0">
              <img src={entry.logo} alt={entry.name} className="h-full w-full object-cover" />
            </div>
            <span className="rq-tag">{entry.name}</span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">{c.title}</h1>
        </div>
      </section>

      {/* INTRO + STICKY FORM */}
      <section className="py-12">
        <div className="rq-container grid lg:grid-cols-3 gap-10 items-start">
          <motion.div {...inView} variants={container} className="lg:col-span-2">
            <motion.p variants={item} className="text-[15px] md:text-[17px] leading-[1.95] text-[color:var(--rq-muted)]">
              {c.intro}
            </motion.p>
          </motion.div>
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <ConsultForm source={`Platform: ${entry.name}`} />
            </div>
          </div>
        </div>
      </section>

      {/* SECTIONS */}
      {sections.map((s, i) => {
        const dark = i % 2 === 0;
        return (
          <section key={i} className={`py-14 ${dark ? "rq-greendark" : "rq-light"}`}>
            <div className="rq-container">
              <motion.div {...inView} variants={container}>
                <motion.span variants={item} className="rq-tag">{entry.name}</motion.span>
                <motion.h2 variants={item} className="font-display mt-3 text-2xl md:text-[34px] font-bold text-white leading-tight">
                  {s.heading}
                </motion.h2>
                {s.paras.map((p, pi) => (
                  <motion.p key={pi} variants={item} className="mt-4 text-[15px] leading-relaxed text-[color:var(--rq-muted)] max-w-4xl">
                    {p}
                  </motion.p>
                ))}
                {s.list && s.list.length > 0 && (
                  <motion.div variants={container} className="mt-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {s.list.map((li, k) => (
                      <motion.div
                        key={k}
                        variants={item}
                        whileHover={{ x: dir === "rtl" ? -4 : 4 }}
                        className="flex items-start gap-3 rounded-xl border border-[color:var(--rq-border)] bg-[rgba(14,122,58,0.06)] px-4 py-3"
                      >
                        <span className="grid place-items-center h-6 w-6 rounded-full bg-[rgba(14,122,58,0.16)] text-[color:var(--rq-green)] shrink-0 mt-0.5">
                          <Check size={13} />
                        </span>
                        <span className="text-sm leading-relaxed text-white">{li}</span>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </motion.div>
            </div>
          </section>
        );
      })}

      {/* OTHER PLATFORMS */}
      <section className="py-12">
        <div className="rq-container">
          <h2 className="font-display text-xl font-semibold text-white mb-6">
            {lang === "ar" ? "منصّات أخرى" : "Explore Other Platforms"}
          </h2>
          <div className="flex flex-wrap gap-3">
            {others.map((s) => (
              <Link
                key={s}
                to={`/platforms/${s}`}
                className="rq-ghost-btn px-4 py-2 text-sm inline-flex items-center gap-2"
                data-testid={`related-platform-${s}`}
              >
                {PLATFORM_DETAILS[s].name}
                <ArrowRight size={14} style={{ transform: flip }} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
